"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Target, MapPin, ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Compass, Zap, Building2, FileSpreadsheet } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const marketStats = [{
  id: "1",
  label: "Local Intent Search Growth",
  value: "+42%",
  description: "YoY increase in high-intent commercial queries across Chelmsford & Mid-Essex."
}, {
  id: "2",
  label: "Map Pack Click Share",
  value: "58%",
  description: "Of total organic clicks go directly to Google Map Pack top 3 listings."
}, {
  id: "3",
  label: "Mobile Search Ratio",
  value: "74%",
  description: "Local service searches performed on smartphones in the region."
}, {
  id: "4",
  label: "Uncaptured Demand",
  value: "61%",
  description: "Of local Chelmsford competitors lack proper technical and entity optimization."
}];
const keywordData = [{
  id: "1",
  keyword: "SEO consultant Chelmsford",
  volume: "320/mo",
  difficulty: "Medium",
  intent: "Transactional",
  opportunity: "High conversion rate for direct local consultancy requests."
}, {
  id: "2",
  keyword: "Chelmsford local business SEO",
  volume: "210/mo",
  difficulty: "Low-Med",
  intent: "Commercial",
  opportunity: "Strong opportunity for targeted local service landing pages."
}, {
  id: "3",
  keyword: "SEO services Essex",
  volume: "880/mo",
  difficulty: "High",
  intent: "Commercial",
  opportunity: "Broad regional traffic stream with high value B2B retainer prospects."
}, {
  id: "4",
  keyword: "Technical SEO audit Chelmsford",
  volume: "140/mo",
  difficulty: "Low",
  intent: "Transactional",
  opportunity: "Immediate technical fix demand for established local e-commerce & B2B brands."
}];
const opportunities = [{
  id: "1",
  title: "Google Business Profile Entity Synergy",
  desc: "I align your website's schema architecture directly with local Chelmsford geo-coordinates to dominate the local top-3 map pack."
}, {
  id: "2",
  title: "Hyper-Local Content Clusters",
  desc: "Building Essex-focused topical authority pages that capture local buying intent before national competitors can react."
}, {
  id: "3",
  title: "Technical Speed & Mobile Readiness",
  desc: "Fixing core web vitals so local mobile searchers enjoy sub-second load times that directly increase inquiry conversions."
}];
const neighboringCities = [{
  id: "1",
  name: "Ipswich",
  path: "/LocalSeoConsultantIpswichPage",
  distance: "42 miles"
}, {
  id: "2",
  name: "Norwich",
  path: "/LocalSeoConsultantNorwichPage",
  distance: "78 miles"
}, {
  id: "3",
  name: "Peterborough",
  path: "/LocalSeoConsultantPeterboroughPage",
  distance: "85 miles"
}, {
  id: "4",
  name: "Milton Keynes",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "65 miles"
}, {
  id: "5",
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "80 miles"
}, {
  id: "6",
  name: "Norwich SEO Services",
  path: "/LocalSeoConsultantNorwichPage",
  distance: "Regional"
}];
export default function Chelmsfordmarketanalysis() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="chelmsfordmarketanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl space-y-16">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary/80 rounded-full inline-flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5" />
            Chelmsford & Essex Search Landscape
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
            Chelmsford Local SEO Market Analysis
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            I continuously monitor search behavior across Chelmsford and Essex to identify untapped organic growth opportunities for businesses seeking direct, expert SEO guidance.
          </p>
        </div>

        {/* Market Stats Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {marketStats.map((stat, i) => <motion.div key={stat.id} data-index={i} variants={itemVariants}>
              <Card className="bg-card text-card-foreground border-border/60 shadow-xs hover:border-primary/30 transition-[border-color,box-shadow] h-full flex flex-col justify-between">
                <CardHeader className="pb-2">
                  <CardDescription className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {stat.label}
                  </CardDescription>
                  <CardTitle className="text-3xl sm:text-4xl font-bold text-primary font-default">
                    {stat.value}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Keyword Trend Table */}
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1">
                <BarChart3 className="w-4 h-4" />
                <span>Search Intelligence</span>
              </div>
              <h3 className="text-2xl font-bold text-foreground font-default">
                High-Value Local Keyword Trends
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Data-backed breakdown of high-intent search terms within the Chelmsford commercial catchment area.
            </p>
          </div>

          <Card className="bg-card text-card-foreground border-border/80 shadow-xs overflow-hidden">
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead className="font-semibold text-foreground">Target Keyword</TableHead>
                    <TableHead className="font-semibold text-foreground">Est. Volume</TableHead>
                    <TableHead className="font-semibold text-foreground">Difficulty</TableHead>
                    <TableHead className="font-semibold text-foreground">Search Intent</TableHead>
                    <TableHead className="font-semibold text-foreground">Consultant Opportunity Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keywordData.map((row, i) => <TableRow key={row.id} data-index={i} className="hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium text-foreground flex items-center gap-2">
                        <Search className="w-3.5 h-3.5 text-primary shrink-0" />
                        {row.keyword}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{row.volume}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs border-border bg-background">
                          {row.difficulty}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{row.intent}</TableCell>
                      <TableCell className="text-xs sm:text-sm text-muted-foreground">
                        {row.opportunity}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Strategy Highlights & CTA Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-2">
              <Badge variant="secondary" className="text-primary font-semibold text-xs">
                Strategic Blueprint
              </Badge>
              <h3 className="text-2xl font-bold text-foreground font-default">
                How I Help Chelmsford Businesses Win Organic Market Share
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                When you work with me, you do not get junior account managers or generic AI templates. You get direct access to my hands-on technical SEO expertise tailored specifically to your business goals and the Essex market.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {opportunities.map((item, i) => <Card key={item.id} data-index={i} className="bg-card border-border/70 shadow-2xs hover:shadow-xs transition-[border-color,box-shadow]">
                  <CardHeader className="p-4 pb-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                      <Zap className="w-4 h-4" />
                    </div>
                    <CardTitle className="text-sm font-semibold text-foreground font-default leading-snug">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {/* Direct CTA Box */}
          <Card className="bg-primary text-primary-foreground border-transparent shadow-md flex flex-col justify-between p-6 rounded-xl">
            <div className="space-y-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-primary-foreground">
                <Target className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-bold font-default leading-tight">
                Want a Tailored Chelmsford SEO Audit?
              </h3>
              <p className="text-primary-foreground/90 text-sm leading-relaxed">
                I will personally review your current search visibility in Chelmsford, analyze your top 3 local competitors, and outline actionable growth quick-wins.
              </p>
            </div>
            
            <div className="pt-6 space-y-3">
              <Button asChild size="lg" className="w-full bg-background text-foreground hover:bg-background/90 shadow-sm font-medium">
                <Link to="/ContactPage">
                  Book Free SEO Consultation
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CaseStudiesPage">
                  View Verified Results
                </Link>
              </Button>
            </div>
          </Card>
        </div>

        <Separator className="my-8" />

        {/* Nearby Cities Grid */}
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h4 className="text-lg font-bold text-foreground font-default flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Regional SEO Services Across Nearby Cities
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                In addition to Chelmsford, I provide dedicated local SEO consultancy for neighboring hubs across East Anglia & the Midlands.
              </p>
            </div>
            <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary/80 self-start sm:self-auto">
              <Link to="/LocalSEOPage">
                All Local SEO Locations
                <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {neighboringCities.map((city, i) => <div key={city.id} data-index={i}>
                <Link to={city.path} className="group block p-3 rounded-lg border border-border bg-card hover:border-primary/40 hover:bg-muted/50 transition-[color,background-color,border-color] text-center">
                  <p className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors font-default truncate">
                    {city.name}
                  </p>
                  <span className="text-[11.5px] text-muted-foreground">
                    {city.distance}
                  </span>
                </Link>
              </div>)}
          </div>
        </div>

      </div>
    </section>;
}